import React, { useEffect, useState } from 'react';
import { isSameDay, isSameWeek, differenceInSeconds } from 'date-fns';
import * as utils from '@chronos/utils';
import * as timeUtil from '@chronos/time-util';
import PropTypes from 'prop-types';

import useTimer from '@chronos/ui-toolkit/hooks/useTimer';
import UserAvatar from '../../UserAvatar';
import Tooltip from '../../Tooltip';
import EditIcon from '../../Icons/EditIcon';
import PlayIcon from '../../Icons/PlayWhiteIcon';
import Button from '../../Button';
import CameraIcon from '../../Icons/BugIcon';
import HideIcon from '../../Icons/HideIcon';
import DeleteConfirmPopover from '../../DeleteConfirmPopover';
import WorklogItemPlaceholder from '../../Placeholders/WorklogItemPlaceholder';
import LabelList from '../../LabelList';
import CommentBlock from './CommentBlock';
import TimeLine from '../TimeLine';
import * as S from './styled';

const dummyUpdate = 10; // seconds

export const isDailyFirstItem = ({ index, worklogs, listItems }) => {
  if (index === 0) {
    return true;
  }
  const worklog = worklogs[listItems[index]] ?? listItems[index];

  // Undefined behavior for unloaded entry
  if (!worklog) {
    return false;
  }

  const prevWorklog = worklogs[listItems[index - 1]] ?? listItems[index - 1];

  if (!prevWorklog) {
    return false;
  }

  // Cut ISO String date part
  /*
  const currentDate = worklog.startedAt.split('T')[0];
  const prevDate = prevWorklog.startedAt.split('T')[0];
  */

  return !isSameDay(worklog.computed.startedAt, prevWorklog.computed.startedAt);
};

export const isWeklyLastItem = ({
  index,
  worklogs,
  listItems,
  weekStartsOn,
}) => {
  if (index === 0) {
    return true;
  }
  const worklog = worklogs[listItems[index]] ?? listItems[index];

  // Undefined behavior for unloaded entry
  if (!worklog) {
    return false;
  }

  const prevWorklog = worklogs[listItems[index - 1]] || listItems[index - 1];

  if (!prevWorklog) {
    return false;
  }

  return !isSameWeek(
    worklog.computed.startedAt,
    prevWorklog.computed.startedAt,
    {
      weekStartsOn,
    }
  );
};

const WorklogItem = ({ index, style, data }) => {
  const {
    worklogs,
    listItems,
    dayPoints,
    weekPoints,
    cards,
    users,
    onlineUsers,
    isShowScreenshotsBtn,
    isShowEditBtn,
    isShowDeleteBtn,
    isShowLabel,
    isLabelExpanded,
    showStartBtn,
    showTimeline,
    showWorklogButtonsDefault,
    onShowScreenshots,
    shouldDisplayDivider,
    shouldDisplayCard,
    onEditWorklog,
    onHideOwnLiveWorklog,
    onDeleteWorklog,
    onClickCardname,
    onClickExpandLabel,
    onClickExpandComment,
    onResizeLabelArea,
    onResizeCommentArea,
    onStartWorklog,
    cardNamePlacement,
    commentPlacement,
    popoverPlacement,
    isMobile,
    isCommentExpanded,
    weekStartsOn,
  } = data;
  const worklog = worklogs[listItems[index]] ?? listItems[index];

  const [showButtons, setShowButtons] = useState(
    showWorklogButtonsDefault || isMobile
  );
  const [offlineMsg, setOfflineMsg] = useState(null);

  useEffect(() => {
    setShowButtons(showWorklogButtonsDefault || isMobile);
  }, [showWorklogButtonsDefault, isMobile]);

  const onMouseEnter = () => {
    if (!showWorklogButtonsDefault && !isMobile) {
      setShowButtons(true);
    }
  };
  const onMouseLeave = () => {
    if (!showWorklogButtonsDefault && !isMobile) {
      setShowButtons(false);
    }
  };

  // It need to rerender item every 10 seconds to proper display paused time
  const [{ state: timerState }, { syncTimer }] = useTimer(
    worklog?.isLive ? dummyUpdate : 0
  );

  useEffect(() => {
    syncTimer(worklog?.timerState);
  }, [worklog?.timerState]);

  const [userOnlineStatus, statusTimeStampStr] = (
    onlineUsers?.[worklog?.accountId] || 'offline'
  ).split(':');

  const isUserOnline = userOnlineStatus === 'online';

  useEffect(() => {
    let msg = null;
    if (!isUserOnline && statusTimeStampStr) {
      msg = 'user offline';
      const seconds = differenceInSeconds(
        +new Date(),
        parseInt(statusTimeStampStr, 10)
      );
      if (seconds >= 60) {
        msg = `${msg} ${timeUtil.stj(seconds)}`;
      }
    }
    setOfflineMsg(msg);
  }, [timerState.dummyTicker]);

  if (!worklog) {
    return (
      <div style={style}>
        <WorklogItemPlaceholder
          index={index}
          showDivider={shouldDisplayDivider}
          shouldDisplayCard={shouldDisplayCard}
          cardNamePlacement={cardNamePlacement}
          commentPlacement={commentPlacement}
        />
      </div>
    );
  }
  const card = cards[worklog.cardId];
  const isEdited = worklog.isLive
    ? false
    : worklog.createdAt !== worklog.updatedAt;

  const isPaused = (worklog.timerState?.pauseStartTime || null) !== null;

  const user = users[worklog.accountId];
  const isUserLoading = user === undefined;

  const accountFullName = user?.fullName || 'Unknown';
  const accountAvatarUrl = user?.avatarUrl ? `${user.avatarUrl}/170.png` : null;
  const accountInitials = utils.getMemberInitials(user?.fullName);

  const creationType = worklog.type === 'auto' ? 'using tracker' : 'manual';
  const trackingStatus = [
    isPaused && 'pause',
    !isPaused && timerState.pauseTrackedSeconds > 60 && 'was paused on',
    timerState.isPauseMinute && timerState.pauseStr,
  ]
    .filter(Boolean)
    .join(' ');

  let { timePoints } = worklog.computed;
  if (isEdited) {
    timePoints = `${timePoints} (edited)`;
  }
  const shouldShowTimeline =
    showTimeline &&
    isDailyFirstItem({
      index,
      worklogs,
      listItems,
    });

  const showWeekPoint =
    showTimeline &&
    shouldShowTimeline &&
    isWeklyLastItem({
      index,
      worklogs,
      listItems,
      weekStartsOn,
    });

  return (
    <div
      style={{
        ...style,
        ...(worklog.saveInProcess
          ? {
              filter: 'blur(5px)',
            }
          : {}),
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {shouldShowTimeline && (
        <TimeLine
          date={worklog.computed.startedAt}
          secondsByDate={dayPoints?.[worklog.computed.startedAtDayStr]}
          secondsByWeek={
            showWeekPoint
              ? weekPoints?.[worklog.computed.endOfWeekDayStr]
              : null
          }
        />
      )}
      <S.WorklogItem>
        <UserAvatar
          src={accountAvatarUrl}
          initials={accountInitials}
          isLoading={isUserLoading}
          /*
          onlineStatus={worklog.isLive ? userOnlineStatus : null}
          */
          size="medium"
        />
        <S.Body>
          <S.Title>
            <S.UserName isLoading={isUserLoading}>{accountFullName}</S.UserName>
            <S.UserAction>
              {worklog.isLive ? (
                <>
                  <S.ActionText>logging</S.ActionText>
                  <S.Dot timerStatus={timerState.timerStatus} />
                  <S.TimerText>
                    {timeUtil.getTimeString(timerState.timer)}
                  </S.TimerText>
                </>
              ) : (
                <S.ActionText>
                  {`logged ${worklog.computed.trackedTime}`}
                </S.ActionText>
              )}
            </S.UserAction>
            {shouldDisplayCard && cardNamePlacement === 'title' && (
              <S.CardInfo>
                to
                <S.CardName
                  onClick={() => {
                    onClickCardname(card);
                  }}
                >
                  {card?.name || 'Unknown card'}
                </S.CardName>
              </S.CardInfo>
            )}
            {commentPlacement === 'title' && worklog.comment && (
              <S.Comment placement={commentPlacement}>
                {worklog.comment}
              </S.Comment>
            )}
            {worklog.isLive && worklog.isOwn && showButtons && (
              <S.ManagePanel>
                <Tooltip title="Hide your own live worklog">
                  <Button
                    appearance="subtle-link"
                    spacing="none"
                    onClick={() => {
                      onHideOwnLiveWorklog(worklog, index);
                    }}
                  >
                    <HideIcon size="middle" />
                  </Button>
                </Tooltip>
              </S.ManagePanel>
            )}
            {!worklog.isLive && showButtons && (
              <S.ManagePanel>
                {showStartBtn && (
                  <Button
                    style={{
                      marginRight: '12px',
                    }}
                    appearance="subtle-link"
                    spacing="none"
                    onClick={() => {
                      if (onStartWorklog) {
                        onStartWorklog(worklog);
                      }
                    }}
                    withIcon
                  >
                    <PlayIcon size="middle" />
                  </Button>
                )}
                {isShowEditBtn && card?.isUnknown !== true && (
                  <Button
                    appearance="subtle-link"
                    spacing="none"
                    onClick={() => {
                      onEditWorklog(worklog, index);
                    }}
                    withIcon
                  >
                    <EditIcon size="middle" />
                  </Button>
                )}
                {worklog.activity?.length > 0 && isShowScreenshotsBtn && (
                  <Button
                    appearance="subtle-link"
                    spacing="none"
                    onClick={() => {
                      onShowScreenshots(worklog.id, worklog.cardId);
                    }}
                  >
                    <CameraIcon size="middle" />
                  </Button>
                )}
                {isShowDeleteBtn && (
                  <DeleteConfirmPopover
                    shouldShowBorder
                    place={popoverPlacement}
                    onDeleteConfirm={() => {
                      onDeleteWorklog(worklog, index);
                    }}
                  />
                )}
              </S.ManagePanel>
            )}
          </S.Title>
          {shouldDisplayCard && cardNamePlacement === 'body' && (
            <S.Title>
              <S.CardInfo>
                to
                <S.CardName
                  onClick={() => {
                    onClickCardname(card);
                  }}
                >
                  {card?.name || 'Unknown card'}
                </S.CardName>
              </S.CardInfo>
            </S.Title>
          )}
          {commentPlacement === 'body' && worklog.comment && (
            <S.Comment placement={commentPlacement}>
              <CommentBlock
                isExpanded={isCommentExpanded(
                  worklog.isLive ? worklog.startTime : worklog.id
                )}
                onResize={(size) => {
                  if (onResizeCommentArea) {
                    onResizeCommentArea(
                      worklog.isLive ? worklog.startTime : worklog.id,
                      index,
                      size
                    );
                  }
                }}
                onExpand={() => {
                  onClickExpandComment(
                    worklog.isLive ? worklog.startTime : worklog.id,
                    index
                  );
                }}
                comment={worklog.comment}
              />
            </S.Comment>
          )}
          {isShowLabel && card?.labels?.length > 0 && (
            <LabelList
              isExpanded={isLabelExpanded}
              labels={card?.labels}
              onResize={(size) => {
                onResizeLabelArea(
                  worklog.isLive ? worklog.startTime : worklog.id,
                  index,
                  size
                );
              }}
              onExpand={onClickExpandLabel}
            />
          )}
          <S.Footer>
            <S.FooterInfo>
              <div>{timePoints}</div>
              {worklog.isLive && trackingStatus && <div>{trackingStatus}</div>}
              {worklog.isLive && offlineMsg && <div>{offlineMsg}</div>}
              {!worklog.isLive && <div>{creationType}</div>}
            </S.FooterInfo>
          </S.Footer>
          {shouldDisplayDivider && (
            <div style={{ marginLeft: '8px' }}>
              <S.Divider />
            </div>
          )}
        </S.Body>
      </S.WorklogItem>
    </div>
  );
};

WorklogItem.defaultProps = {
  style: {},
};

WorklogItem.propTypes = {
  index: PropTypes.number.isRequired,
  style: PropTypes.object,
  data: PropTypes.shape({
    worklogs: PropTypes.object.isRequired,
    listItems: PropTypes.object.isRequired,
    cards: PropTypes.object.isRequired,
    users: PropTypes.object.isRequired,
    isShowLabel: PropTypes.bool.isRequired,
    isLabelExpanded: PropTypes.bool.isRequired,
    isShowScreenshotsBtn: PropTypes.bool.isRequired,
    isShowEditBtn: PropTypes.bool.isRequired,
    isShowDeleteBtn: PropTypes.bool.isRequired,
    showStartBtn: PropTypes.bool.isRequired,
    showWorklogButtonsDefault: PropTypes.bool.isRequired,
    shouldDisplayDivider: PropTypes.bool.isRequired,
    shouldDisplayCard: PropTypes.bool.isRequired,
    onShowScreenshots: PropTypes.func,
    onEditWorklog: PropTypes.func.isRequired,
    onHideOwnLiveWorklog: PropTypes.func.isRequired,
    onDeleteWorklog: PropTypes.func.isRequired,
    onClickCardname: PropTypes.func.isRequired,
    onClickExpandLabel: PropTypes.func,
    onClickExpandComment: PropTypes.func.isRequired,
    onResizeLabelArea: PropTypes.func.isRequired,
    onResizeCommentArea: PropTypes.func,
    onStartWorklog: PropTypes.func,
    cardNamePlacement: PropTypes.oneOf(['title', 'body']),
    commentPlacement: PropTypes.oneOf(['title', 'body']),
    dayPoints: PropTypes.object,
    weekPoints: PropTypes.object,
    weekStartsOn: PropTypes.number,
    onlineUsers: PropTypes.object,
    showTimeline: PropTypes.bool,
    isMobile: PropTypes.bool,
    isCommentExpanded: PropTypes.func.isRequired,
    popoverPlacement: PropTypes.oneOf([
      'start',
      'end',
      'above',
      'right',
      'below',
      'left',
      'column',
      'row',
    ]),
  }).isRequired,
};

export default WorklogItem;
